import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Cookies from "js-cookie";
import "./SingleField.scss";

import { fetchData, fetchWaterSensor } from "../../APICalls/GetRequests";

// Components
import ConfigurePopup from "../ConfigurePopup/ConfigurePopup";
import PipesBar from "../PipesBar/PipesBar";
import AddZonesPopup from "../AddZonesPopup/AddZonesPopup";
import ZoneDropdown from "../ZoneDropdown/ZoneDropdown";
import DeleteFieldPopup from "../DeleteFieldPopup/DeleteFieldPopup";
import SinglePipePopup from "../SinglePipePopup/SinglePipePopup";
import FieldNamePopup from "../FieldNamePopup/FieldNamePopup";
import AddWaterSensorPopup from "../AddWaterSensorPopup/AddWaterSensorPopup";
import WaterSensorPopup from "../WaterSensorPopup/WaterSensorPopup";
import WaterSensorCard from "../WaterSensorCard/WaterSensorCard";
import Spinner from "../Spinner/Spinner";

const SingleField = ({
  setSelectedZone,
  setSelectedSchedule,
  zones,
  setZones,
  setSideNavFields,
}) => {
  const { fieldId } = useParams();

  const [selectedField, setSelectedField] = useState(null);
  const [pipes, setPipes] = useState([]);
  const [waterSensors, setWaterSensors] = useState([]);
  const [zonesLoading, setZonesLoading] = useState(true);
  const [fieldLoading, setFieldLoading] = useState(true);
  const [pipesLoading, setPipesLoading] = useState(true);

  const [showConfigurePopup, setShowConfigurePopup] = useState(false);
  const [showAddZonesPopup, setShowAddZonesPopup] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [showPipePopup, setShowPipePopup] = useState(false);
  const [showFieldNamePopup, setShowFieldNamePopup] = useState(false);

  const [showAddWaterSensorPopup, setShowAddWaterSensorPopup] = useState(false);
  const [showWaterSensorPopup, setShowWaterSensorPopup] = useState(false);
  const [selectedWaterSensorId, setSelectedWaterSensorId] = useState(null);

  const [pipeId, setPipeId] = useState(null);
  const [numOfPipes, setNumOfPipes] = useState(0);

  // Fetch data
  useEffect(() => {
    if (!fieldId) return;

    fetchData("singleField", Cookies.get("session_key"), fieldId)
      .then(setSelectedField)
      .catch(() => setSelectedField(null))
      .finally(() => setFieldLoading(false));

    fetchData("allZones", Cookies.get("session_key"), fieldId)
      .then(setZones)
      .catch(() => setZones([]))
      .finally(() => setZonesLoading(false));

    fetchData("allPipes", Cookies.get("session_key"), fieldId)
      .then(setPipes)
      .catch(() => setPipes([]))
      .finally(() => setPipesLoading(false));

    fetchWaterSensor(Cookies.get("session_key"), fieldId)
      .then(setWaterSensors)
      .catch(() => setWaterSensors([]));
  }, [fieldId, setZones]);

  const refreshPipes = () => {
    fetchData("allPipes", Cookies.get("session_key"), fieldId)
      .then(setPipes)
      .catch(() => setPipes([]));
  };

  const openWaterSensorPopup = (sensorId) => {
    setSelectedWaterSensorId(sensorId);
    setShowWaterSensorPopup(true);
  };

  const refreshWaterSensors = () => {
    fetchWaterSensor(Cookies.get("session_key"), fieldId)
      .then(setWaterSensors)
      .catch(() => setWaterSensors([]));
  };


  const isZonesEmpty = zones.length < 1;

  if (!fieldId) return <h1>Invalid Field ID</h1>;

  return (
    <div className="SingleField">
      {fieldLoading ? (
        <Spinner color="blue" />
      ) : (
        <div className="field-name-container">
          <h2
            className="field-name"
            onClick={() => setShowFieldNamePopup(true)}
          >
            Field: {selectedField?.name}
          </h2>

          {!zonesLoading && (
            <ZoneDropdown
              disabled={isZonesEmpty}
              zones={zones}
              field={selectedField}
              setSelectedZone={setSelectedZone}
            />
          )}
        </div>
      )}

      <h3 className="pipes-header">Pipes</h3>
      <div className="pipes-container">
        {pipesLoading ? (
          <Spinner color="blue" />
        ) : pipes.length ? (
          <PipesBar
            fieldId={selectedField?.id}
            pipes={pipes}
            setPipeId={setPipeId}
            handlePipePopupOpen={() => setShowPipePopup(true)}
          />
        ) : (
          <p>No pipes available.</p>
        )}
      </div>

      <h3>Water Sensors</h3>
      <div className="water-sensors-container">
        {waterSensors.length ? (
          [...waterSensors]
            .sort((a, b) => a.local_id - b.local_id)
            .map((sensor) => (
              <WaterSensorCard
                key={sensor.id}
                sensor={sensor}
                onClick={openWaterSensorPopup}
              />
            ))
        ) : (
          <p className="empty-state">No water sensors added.</p>
        )}
      </div>
      <div className="singlefield-button-container">
        <button
          className="configure-button"
          onClick={() => setShowConfigurePopup(true)}
        >
          Sequence (Mobile Only)
        </button>

        {pipes.length > 0 && (
          <button
            className="add-zone-button"
            onClick={() => setShowAddZonesPopup(true)}
          >
            Add Zone
          </button>
        )}

        <button
          className="delete-field-button"
          onClick={() => setShowDeletePopup(true)}
        >
          Delete Field
        </button>

        <button
          className="add-zone-button"
          onClick={() => setShowAddWaterSensorPopup(true)}
        >
          Add Water Sensor
        </button>
      </div>
      {/* Popups */}
      {/* Configure Popup */}
      {showConfigurePopup && selectedField && (
        <ConfigurePopup
          isVisible
          field={selectedField}
          handleClosePopup={() => setShowConfigurePopup(false)}
          handleNumOfPipesChange={setNumOfPipes}
          setPipes={setPipes}
        />
      )}

      {/* Add Zones Popup */}
      {showAddZonesPopup && selectedField && (
        <AddZonesPopup
          isVisible
          setZones={setZones}
          field={selectedField}
          pipes={pipes}
          handleCloseAddZonesPopup={() => setShowAddZonesPopup(false)}
        />
      )}

      {/* Delete Field Popup */}
      {showDeletePopup && selectedField && (
        <DeleteFieldPopup
          isVisible
          field={selectedField}
          handleCloseDeleteField={() => setShowDeletePopup(false)}
        />
      )}

      {/* Pipe Popup */}
      {showPipePopup && selectedField && pipeId && (
        <SinglePipePopup
          field={selectedField}
          pipes={pipes}
          pipeId={pipeId}
          handlePipePopupClose={() => setShowPipePopup(false)}
          setPipes={setPipes}
          refreshPipes={refreshPipes}
        />
      )}

      {/* Field Name Popup */}
      {selectedField && (
  <FieldNamePopup
    isVisible={showFieldNamePopup}
    field={selectedField}
    handleFieldNamePopupClose={() => setShowFieldNamePopup(false)}
    onFieldNameUpdated={(name) => {
      setSelectedField((prev) => ({ ...prev, name }));
      setSideNavFields?.((prev) =>
        prev.map((f) =>
          f.id === Number(fieldId) ? { ...f, name } : f
        )
      );
    }}
  />
)}

      {/* Add Water Sensor Popup */}
      {showAddWaterSensorPopup && selectedField && (
        <AddWaterSensorPopup
          field={selectedField}
          onClose={() => setShowAddWaterSensorPopup(false)}
        />
      )}

      {/* Water Sensor Popup */}
      {showWaterSensorPopup && selectedField && selectedWaterSensorId && (
        <WaterSensorPopup
          isVisible
          sensorId={selectedWaterSensorId}
          fieldId={selectedField.id}
          onClose={() => {
            setShowWaterSensorPopup(false);
            setSelectedWaterSensorId(null);
          }}
          onSensorUpdated={refreshWaterSensors}
        />
      )}
    </div>
  );
};

export default SingleField;
